import { Injectable } from '@angular/core';
import { AuthService, RoutingNames, UserRoles, WntRoutingNames } from 'shared';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { firstValueFrom, map } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class RoleRedirectGuard {
  constructor(
    private auth: AuthService,
    private router: Router,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    const isLoggedin = await firstValueFrom(this.auth.isLoggedIn());
    // Force the user to log in if currently unauthenticated.
    if (!isLoggedin) {
      return this.router.createUrlTree([WntRoutingNames.publicInstitutions]);
    }

    const roles = await firstValueFrom(this.auth.getRoles());

    // Admins: always redirect to public
    if (roles.includes(UserRoles.wntAdmin)) {
      return this.router.createUrlTree([WntRoutingNames.publicInstitutions]);
    }

    // Supervisor: always redirect to institution list
    if (roles.includes(UserRoles.wntSupervisor) || roles.includes(UserRoles.wntInstitutionAdmin)) {
      return this.router.createUrlTree([WntRoutingNames.institutions]);
    }

    // Institution user logic
    if (roles.includes(UserRoles.wntInstitution)) {
      const userInstitutionId = await firstValueFrom(this.auth.getAttributes().pipe(map((attrs) => attrs.institution)));

      const routeInstitutionId = route.params['institutionId'];

      // If this is a route with an institutionId (edit/submit)
      if (routeInstitutionId) {
        // Block access if institutionId doesn't match
        if (routeInstitutionId !== userInstitutionId) {
          return this.router.createUrlTree([RoutingNames.permissionDenied]);
        }

        // Let them through to their institution route
        return true;
      }

      // If we're at the root or somewhere generic, redirect to /edit/:institutionId
      return this.router.createUrlTree([WntRoutingNames.root, WntRoutingNames.editing, userInstitutionId]);
    }

    // Admins and other roles: allow through or handle differently
    if (roles.includes(UserRoles.wntAdmin)) {
      return true; // Allow admins to access all routes freely
    }

    // Default: block access
    return this.router.createUrlTree([RoutingNames.permissionDenied]);
  }
}
